import { Link } from 'react-router-dom';
import { ArrowLeftIcon, RocketIcon, HeartIcon, UsersIcon, GlobeIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import Button from '@/components/form/Button';
import { useAuth } from '@/context/AuthContext';

const teamMembers = [
  {
    name: 'Dennis Horn',
    role: 'Founder & CEO',
    image: './images/dennis-horn.jpg', // Updated to use relative path
    description: 'Passionate about using technology to help people make better decisions together.'
  }
];

const values = [
  {
    icon: <RocketIcon className="h-8 w-8 text-primary-600" />,
    title: 'Innovation First',
    description: 'We constantly push boundaries to bring you the most advanced decision-making tools.'
  },
  {
    icon: <HeartIcon className="h-8 w-8 text-red-600" />,
    title: 'Community Driven',
    description: 'Our platform grows and evolves based on our vibrant community´s needs and feedback.'
  },
  {
    icon: <UsersIcon className="h-8 w-8 text-green-600" />,
    title: 'Inclusive Decision Making',
    description: 'We believe the best decisions are made when everyone has a voice.'
  },
  {
    icon: <GlobeIcon className="h-8 w-8 text-blue-600" />,
    title: 'Global Impact',
    description: 'Making the world better one decision at a time, connecting minds across borders.'
  }
];

export default function About() {
  const { user } = useAuth();
  const navigateBack = () => window.location.href = user ? '/dashboard' : '/';

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          size="sm"
          icon={<ArrowLeftIcon className="h-4 w-4" />}
          onClick={navigateBack}
          className="mb-12"
        >
          Back to {user ? 'Dashboard' : 'Home'}
        </Button>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl lg:text-6xl">
            About <span className="text-primary-600">SnapVote</span>
          </h1>
          <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            We're on a mission to revolutionize how people make decisions together, combining collective wisdom with artificial intelligence.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <h2 className="text-3xl font-bold text-gray-900">Our Story</h2>
            <p className="text-lg text-gray-600">
              SnapVote was born from a simple observation: making decisions in groups is hard. 
              Whether it's choosing a restaurant for dinner or making important business decisions, 
              getting everyone's input and reaching a consensus can be challenging.
            </p>
            <p className="text-lg text-gray-600">
              We built SnapVote to solve this problem by combining the power of collective wisdom 
              with artificial intelligence. Our platform makes it easy to create polls, gather feedback, 
              and make informed decisions together.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="relative"
          >
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80"
              alt="Team collaboration"
              className="rounded-2xl shadow-xl"
            />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center mb-4">
                  {value.icon}
                  <h3 className="text-xl font-bold text-gray-900 ml-3">{value.title}</h3>
                </div>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-12">Our Team</h2>
          <div className="grid grid-cols-1 gap-8 max-w-lg mx-auto">
            {teamMembers.map((member) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                  onError={(e) => {
                    const img = e.target as HTMLImageElement;
                    img.src = 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png?20150327203541';
                  }}
                />
                <h3 className="text-xl font-bold text-gray-900">{member.name}</h3>
                <p className="text-primary-600 font-medium mb-2">{member.role}</p>
                <p className="text-gray-600">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <Button
            variant="primary"
            size="lg"
            onClick={() => window.location.href = user ? '/create' : '/register'}
            className="text-lg"
          >
            {user ? 'Create Your First SNAP' : 'Join Our Community'}
          </Button>
        </motion.div>
      </div>
    </div>
  );
}